import { useState } from "react"
import { useMutation, useSuspenseQuery } from "@tanstack/react-query"
import { Link, createFileRoute } from "@tanstack/react-router"
import { ArrowLeft, ArrowRight, Check, Link2, Play, ShieldCheck, Users } from "lucide-react"
import { toast } from "sonner"
import type { SimulateInput, SimulationResult } from "@vienna/contracts"
import { Avatar, AvatarFallback } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { AutoHeading, Choice } from "@/components/automations/primitives"
import { api, ApiError } from "@/lib/api"
import { initials } from "@/lib/format"
import { automationQuery } from "@/lib/queries"
import { useWorkspaceContext } from "@/lib/session"

export const Route = createFileRoute("/w/$workspace/automations/$automationId/simulate")({ component: Simulation })

const SCENARIOS = [
  { value: "match_reply", label: "Keyword match + reply" },
  { value: "match_timeout", label: "Keyword match, no reply" },
  { value: "no_match", label: "No keyword match" },
  { value: "human_owned", label: "Human already owns contact" },
  { value: "duplicate_entry", label: "Duplicate trigger event" },
] as const

/** Screen 20. Runs the server simulation: same walker as execution, recording effects only. */
function Simulation() {
  const { workspace } = useWorkspaceContext()
  const { automationId } = Route.useParams()
  const automation = useSuspenseQuery(automationQuery(workspace.slug, automationId)).data
  const params = { workspace: workspace.slug }
  const [scenario, setScenario] = useState<SimulateInput["scenario"]>("match_reply")
  const [comment, setComment] = useState("GUIDE please!")
  const [name, setName] = useState("Maya")
  const [reply, setReply] = useState("I’d love some help choosing.")
  const run = useMutation({
    mutationFn: () => api<SimulationResult>(`/w/${workspace.slug}/automations/${automationId}/simulate`, { method: "POST", body: JSON.stringify({ scenario, commentText: comment, firstName: name, replyText: reply }) }),
    onSuccess: () => toast.success("Simulation complete", { description: "No messages were sent." }),
    onError: (error) => toast.error(error instanceof ApiError ? error.problem.title : "Simulation failed"),
  })
  const result = run.data
  const version = automation.draft ? `draft v${automation.draft.number}` : automation.live ? `live v${automation.live.number}` : "draft"
  return (
    <div className="page-wrap auto-page">
      <AutoHeading
        title="Test the conversation"
        description="Explore every branch with sample data."
        back={
          <Button variant="ghost" size="sm" className="mb-3 -ml-2" asChild>
            <Link to="/w/$workspace/automations/$automationId" params={{ ...params, automationId }}>
              <ArrowLeft />
              {automation.name}
            </Link>
          </Button>
        }
      >
        <Button variant="outline" asChild>
          <Link to="/w/$workspace/automations/$automationId/publish" params={{ ...params, automationId }}>
            Review publication
            <ArrowRight />
          </Link>
        </Button>
        <Button onClick={() => run.mutate()} disabled={run.isPending}>
          <Play />
          Run simulation
        </Button>
      </AutoHeading>
      <div className="mb-6 flex items-center gap-3 rounded-lg border bg-muted/40 px-4 py-3">
        <Play className="size-4" />
        <strong className="text-sm font-medium">Simulation mode</strong>
        <span className="text-xs text-muted-foreground">No messages sent. No real contacts changed.</span>
      </div>
      <div className="auto-simulation">
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Scenario</CardTitle>
          </CardHeader>
          <CardContent className="space-y-5">
            <div className="space-y-2">
              <Label>Test case</Label>
              <Choice
                label="Test case"
                value={scenario}
                onChange={(value) => {
                  setScenario(value)
                  if (value === "no_match") setComment("Beautiful studio!")
                  else if (comment === "Beautiful studio!") setComment("GUIDE please!")
                }}
                values={SCENARIOS}
                className="w-full"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="simulation-comment">Comment text</Label>
              <Input id="simulation-comment" value={comment} onChange={(e) => setComment(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="simulation-name">Sample first name</Label>
              <Input id="simulation-name" value={name} onChange={(e) => setName(e.target.value)} />
            </div>
            <div className="space-y-2">
              <Label htmlFor="simulation-reply">Sample reply</Label>
              <Input id="simulation-reply" value={reply} onChange={(e) => setReply(e.target.value)} />
            </div>
            <div className="rounded-lg bg-muted/50 p-3 text-xs leading-5 text-muted-foreground">
              Channel: {automation.provider === "messenger" ? "Messenger" : "Instagram"}
              <br />
              Eligibility: simulated
              <br />
              Version: {version}
            </div>
            <Button
              variant="outline"
              className="w-full"
              onClick={() => {
                setScenario("match_reply")
                setComment("GUIDE please!")
                setName("Maya")
                run.reset()
              }}
            >
              Reset sample data
            </Button>
          </CardContent>
        </Card>
        <Card className="gap-0">
          <CardHeader className="border-b">
            <div className="flex items-center gap-3">
              <Avatar>
                <AvatarFallback>{initials(`${name || "Sample"} Johnson`)}</AvatarFallback>
              </Avatar>
              <div>
                <CardTitle>{name || "Sample contact"} Johnson</CardTitle>
                <CardDescription>Customer preview · {automation.provider === "messenger" ? "Messenger" : "Instagram"}</CardDescription>
              </div>
            </div>
          </CardHeader>
          <CardContent className="auto-chat-preview">
            <span className="auto-chat-divider">Post comment</span>
            <div className="auto-chat-bubble is-customer">{scenario === "no_match" ? "Beautiful studio!" : comment}</div>
            {result && result.messages.length > 0 ? (
              <>
                {result.messages.map((m, i) => (
                  <span key={i} className="contents">
                    {m.kind === "private_message" && i > 0 && <span className="auto-chat-divider">Private conversation</span>}
                    <span className="text-xs text-muted-foreground">{m.kind === "public_reply" ? "Public reply" : m.kind === "private_message" ? "Private message" : "Message"} · Automation</span>
                    <div className="auto-chat-bubble">
                      {m.text}
                      {m.button && (
                        <Button variant="outline" className="mt-4 w-full" asChild>
                          <a href={m.button.url} target="_blank" rel="noreferrer">
                            <Link2 />
                            {m.button.label}
                          </a>
                        </Button>
                      )}
                    </div>
                  </span>
                ))}
                {scenario === "match_reply" && <div className="auto-chat-bubble is-customer">{reply}</div>}
                {result.handedOver && (
                  <span className="auto-chat-divider">
                    <Users className="size-3" />
                    Routed to your team
                  </span>
                )}
              </>
            ) : (
              <div className="my-12 rounded-lg border border-dashed p-5 text-center">
                <ShieldCheck className="mx-auto mb-3 size-5 text-muted-foreground" />
                <p className="font-medium">{result ? "No automated messages" : "Run the simulation"}</p>
                <p className="mt-2 text-xs leading-5 text-muted-foreground">{result ? result.trace.at(-1)?.detail : "Choose a scenario and run it to see the customer's view."}</p>
              </div>
            )}
          </CardContent>
        </Card>
        <Card className="self-start">
          <CardHeader>
            <CardTitle>Execution trace</CardTitle>
            <CardDescription>Sample outcomes, step by step.</CardDescription>
          </CardHeader>
          <CardContent>
            {result && (
              <Badge variant="outline" className="mb-6">
                <Check className="size-3" />
                Simulation {result.passed ? "passed" : "stopped"}
              </Badge>
            )}
            <div className="auto-trace">
              {(result?.trace ?? []).map((step, i) => (
                <div className="auto-trace-step" key={`${step.nodeId ?? "x"}-${i}`}>
                  <Check className="size-4" />
                  <div>
                    <p className="text-sm font-medium">{step.title}</p>
                    <p className="mt-1 text-xs text-muted-foreground">{step.detail}</p>
                  </div>
                </div>
              ))}
              {!result && <p className="text-sm text-muted-foreground">The trace appears after a run.</p>}
            </div>
            <p className="mt-7 text-xs leading-5 text-muted-foreground">Provider delivery is not tested in simulation.</p>
          </CardContent>
        </Card>
      </div>
    </div>
  )
}
